/**
 * ICS Parser Utility
 * Parses ICS calendar feeds and extracts event information
 */
class ICSParser {
  constructor() {
    this.dateRegex = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/;
    this.dateOnlyRegex = /^(\d{4})(\d{2})(\d{2})$/;
  }

  /**
   * Parse ICS content and return structured event data
   * @param {string} icsContent - Raw ICS file content
   * @param {number} maxEvents - Maximum number of events to return
   * @param {number} daysAhead - How many days ahead to look for events
   * @returns {Array} Array of event objects
   */
  parse(icsContent, maxEvents = 10, daysAhead = 365) {
    if (!icsContent || typeof icsContent !== 'string') {
      console.warn('Invalid ICS content provided');
      return [];
    }

    const events = [];
    const lines = icsContent.split(/\r?\n/);
    let currentEvent = null;
    let inEvent = false;

    const now = new Date();
    const cutoffDate = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === 'BEGIN:VEVENT') {
        inEvent = true;
        currentEvent = {
          summary: '',
          description: '',
          dtstart: null,
          dtstartType: null,
          dtend: null,
          dtendType: null,
          location: '',
          url: '',
          uid: ''
        };
        continue;
      }

      if (line === 'END:VEVENT' && inEvent) {
        if (currentEvent && this.isValidEvent(currentEvent, now, cutoffDate)) {
          events.push(this.formatEvent(currentEvent));
        }
        inEvent = false;
        currentEvent = null;
        continue;
      }

      if (!inEvent || !currentEvent) continue;

      // Handle multi-line properties (continuation lines start with space or tab)
      let fullLine = line;
      while (i + 1 < lines.length && /^[\s\t]/.test(lines[i + 1])) {
        i++;
        fullLine += lines[i].trim();
      }

      this.parseEventProperty(fullLine, currentEvent);
    }

    // Sort by start date and limit results
    return events
      .sort((a, b) => a.sortTime - b.sortTime)
      .slice(0, maxEvents)
      .map(({ sortTime, ...event }) => event);
  }

  /**
   * Parse individual event properties
   * @param {string} line - Property line from ICS
   * @param {object} event - Current event object
   */
  parseEventProperty(line, event) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return;

    const property = line.substring(0, colonIndex);
    const value = line.substring(colonIndex + 1);

    // Handle property parameters (e.g., DTSTART;TZID=America/New_York:...)
    const [propertyName, ...parameterParts] = property.split(';');
    const parameters = this.parsePropertyParameters(parameterParts);

    switch (propertyName.toUpperCase()) {
      case 'SUMMARY':
        event.summary = this.unescapeText(value);
        break;
      case 'DESCRIPTION':
        event.description = this.unescapeText(value);
        break;
      case 'DTSTART': {
        const parsedDate = this.parseDate(value, parameters);
        event.dtstart = parsedDate ? parsedDate.date : null;
        event.dtstartType = parsedDate ? parsedDate.type : null;
        break;
      }
      case 'DTEND': {
        const parsedDate = this.parseDate(value, parameters);
        event.dtend = parsedDate ? parsedDate.date : null;
        event.dtendType = parsedDate ? parsedDate.type : null;
        break;
      }
      case 'LOCATION':
        event.location = this.unescapeText(value);
        break;
      case 'URL':
        event.url = value;
        break;
      case 'UID':
        event.uid = value;
        break;
    }
  }

  /**
   * Parse ICS property parameters
   * @param {Array<string>} parameterParts - Property parameters
   * @returns {Object} Parsed parameters
   */
  parsePropertyParameters(parameterParts = []) {
    return parameterParts.reduce((parameters, part) => {
      const [name, value] = part.split('=');

      if (!name) {
        return parameters;
      }

      parameters[name.toUpperCase()] = value || true;
      return parameters;
    }, {});
  }

  /**
   * Parse ICS date format to JavaScript Date
   * @param {string} dateString - ICS formatted date
   * @param {Object} parameters - Property parameters
   * @returns {{ date: Date, type: string }|null} Parsed date metadata or null if invalid
   */
  parseDate(dateString, parameters = {}) {
    if (!dateString) return null;

    const valueType = String(parameters.VALUE || '').toUpperCase();
    if (valueType === 'DATE') {
      const date = this.parseDateOnly(dateString);
      return date ? { date, type: 'date' } : null;
    }

    // Handle date-time format: YYYYMMDDTHHMMSSZ
    let match = dateString.match(this.dateRegex);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return {
        date: new Date(Date.UTC(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
          parseInt(hour, 10),
          parseInt(minute, 10),
          parseInt(second, 10)
        )),
        type: 'date-time'
      };
    }

    // Handle date-only format: YYYYMMDD
    const date = this.parseDateOnly(dateString);
    if (date) {
      return {
        date,
        type: 'date'
      };
    }

    console.warn('Could not parse date:', dateString);
    return null;
  }

  /**
   * Parse a date-only ICS value as a local calendar day without timezone conversion
   * @param {string} dateString - YYYYMMDD date string
   * @returns {Date|null} Local Date instance or null if invalid
   */
  parseDateOnly(dateString) {
    const match = dateString.match(this.dateOnlyRegex);
    if (!match) return null;

    const [, year, month, day] = match;

    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10)
    );
  }

  /**
   * Unescape ICS text content
   * @param {string} text - Escaped ICS text
   * @returns {string} Unescaped text
   */
  unescapeText(text) {
    if (!text) return '';

    return text
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\;/g, ';')
      .replace(/\\,/g, ',')
      .replace(/\\\\/g, '\\');
  }

  /**
   * Check if event is valid and within date range
   * @param {object} event - Event object
   * @param {Date} now - Current date
   * @param {Date} cutoffDate - Maximum date to include
   * @returns {boolean} Whether event is valid
   */
  isValidEvent(event, now, cutoffDate) {
    if (!event.dtstart || !event.summary) {
      return false;
    }

    const startDate = new Date(event.dtstart);

    // Skip past events (events that ended before now)
    const endDate = event.dtend ? new Date(event.dtend) : startDate;
    if (endDate < now) {
      return false;
    }

    // Skip events beyond cutoff date
    if (startDate > cutoffDate) {
      return false;
    }

    return true;
  }

  /**
   * Format event for frontend consumption
   * @param {object} rawEvent - Raw parsed event
   * @returns {object} Formatted event object
   */
  formatEvent(rawEvent) {
    const startDate = new Date(rawEvent.dtstart);
    const endDate = rawEvent.dtend ? new Date(rawEvent.dtend) : null;
    const isAllDay = this.isAllDayEvent(rawEvent.dtstart, rawEvent.dtend, rawEvent.dtstartType);

    return {
      id: rawEvent.uid || this.generateEventId(rawEvent),
      title: rawEvent.summary || 'Untitled Event',
      description: rawEvent.description || '',
      startDate: this.serializeDate(startDate, rawEvent.dtstartType),
      endDate: endDate ? this.serializeDate(endDate, rawEvent.dtendType) : null,
      startDateType: rawEvent.dtstartType || 'date-time',
      endDateType: rawEvent.dtendType || null,
      location: rawEvent.location || '',
      url: rawEvent.url || '',
      isAllDay,
      formattedDate: this.formatDisplayDate(startDate, endDate, {
        isAllDay,
        endType: rawEvent.dtendType
      }),
      formattedTime: this.formatDisplayTime(startDate, endDate, { isAllDay }),
      sortTime: startDate.getTime()
    };
  }

  /**
   * Check if event is all-day
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @param {string|null} startType - Start date value type
   * @returns {boolean} Whether event is all-day
   */
  isAllDayEvent(start, end, startType = null) {
    if (!start) return false;
    if (startType === 'date') return true;

    const startDate = new Date(start);
    return startDate.getUTCHours() === 0 &&
           startDate.getUTCMinutes() === 0 &&
           startDate.getUTCSeconds() === 0;
  }

  /**
   * Format date for display
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @param {Object} options - Display options
   * @returns {string} Formatted date string
   */
  formatDisplayDate(start, end, options = {}) {
    const localeOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const startStr = start.toLocaleDateString(undefined, localeOptions);
    const displayEnd = this.getDisplayEndDate(end, options);

    if (!displayEnd || start.toDateString() === displayEnd.toDateString()) {
      return startStr;
    }

    return `${startStr} - ${displayEnd.toLocaleDateString(undefined, localeOptions)}`;
  }

  /**
   * Format time for display
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @param {Object} options - Display options
   * @returns {string} Formatted time string
   */
  formatDisplayTime(start, end, options = {}) {
    if (options.isAllDay) {
      return 'All day';
    }

    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    const startStr = start.toLocaleTimeString(undefined, timeOptions);

    if (!end) {
      return startStr;
    }

    const endStr = end.toLocaleTimeString(undefined, timeOptions);
    return `${startStr} - ${endStr}`;
  }

  /**
   * Serialize dates for frontend consumption
   * @param {Date} date - Date to serialize
   * @param {string|null} dateType - ICS value type
   * @returns {string|null} Serialized date
   */
  serializeDate(date, dateType) {
    if (!date) return null;

    if (dateType === 'date') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return date.toISOString();
  }

  /**
   * Convert an exclusive all-day end date into its display day
   * @param {Date|null} end - Event end date
   * @param {Object} options - Display options
   * @returns {Date|null} Display end date
   */
  getDisplayEndDate(end, options = {}) {
    if (!end) return null;

    if (options.isAllDay && options.endType === 'date') {
      return new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
    }

    return end;
  }

  /**
   * Generate a unique ID for events without UID
   * @param {object} event - Event object
   * @returns {string} Generated ID
   */
  generateEventId(event) {
    const str = `${event.summary}-${event.dtstart}-${event.location}`;
    return btoa(str).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  }
}

// Export for use in other scripts
window.ICSParser = ICSParser;
